<template>
  <v-card elevation="2" class="pa-6 d-flex" v-if="!loading">
    <div class="d-flex flex-column justify-space-between" style="width: 40%;">
      <p
        class="ma-0 pa-0 body-2 blue-grey--text text--darken-1 mb-4 align-start justify-start"
        v-snip="4"
      >
        {{ routineDefinition.description }}
      </p>

      <div>
        <ReminderChip
          v-if="hasReminder"
          :recurrence="reminderRecurrence"
          :reminderId="reminderId"
          :isRepeatedReminder="isRepeatedReminder"
          :isLoadingReminder="isReminderLoading"
          @deleteReminder="handleDeleteReminder"
          @openReminderModal="showRoutineReminder"
        />
        <v-btn
          v-else
          rounded
          small
          text
          color="blue"
          :loading="isReminderLoading"
          @click="showRoutineReminderDialog = true"
        >
          <v-icon size="16" class="me-2">
            mdi-bell-ring-outline
          </v-icon>
          <p class="ma-0 pa-0">
            {{ $t('routines_reminder.set_reminder') }}
          </p>
        </v-btn>
      </div>

      <RoutineDefinitionDialog
        :show="showDetailsDialog"
        :routineDefinition="routineDefinition"
        @close="showDetailsDialog = false"
      />
    </div>

    <v-divider vertical class="mx-6" />

    <div style="width: 60%;" class="d-flex justify-space-between">
      <div class="d-flex flex-column">
        <div class="d-flex justify-space-between">
          <p class="overline blue-grey--text text--darken-3">
            {{ $t('common.assignee_other') }}
          </p>
        </div>

        <div>
          <div class="d-flex" v-if="routineDefinition.isGloballyAssigned">
            <v-chip small class="routine-global-chip">
              <v-icon size="18" color="blue" class="me-1">mdi-earth </v-icon>
              <p class="ma-0 pa-0 blue--text font-size-12">
                {{ $t('common.global') }}
              </p>
            </v-chip>
          </div>

          <div class="d-flex" v-else>
            <div v-if="hasAssignees" class="d-flex flex-column me-4">
              <p class="caption blue-grey--text text--darken-1">
                {{
                  $t('routines.user_count', {
                    count: assignees.length,
                  })
                }}
              </p>

              <AssigneesSelection
                disabled
                :accounts="assignees"
                :maxItemsToShow="5"
                @click="trackAllItems({ assignees: assignees.length })"
              />
            </div>

            <div v-if="assignedTeams.length" class="d-flex flex-column me-4">
              <p class="caption blue-grey--text text--darken-1">
                {{
                  $t('routines.team_count', {
                    count: assignedTeams.length,
                  })
                }}
              </p>
              <ChipList
                :items="assignedTeams"
                :limit="1"
                outlined
                @click="trackAllItems({ assignedTeams: assignedTeams.length })"
              />
            </div>
            <div v-if="assignedSites.length" class="d-flex flex-column">
              <p class="caption blue-grey--text text--darken-1">
                {{
                  $t('routines.site_count', {
                    count: assignedSites.length,
                  })
                }}
              </p>
              <ChipList
                :items="assignedSites"
                :limit="1"
                outlined
                @click="trackAllItems({ assignedSites: assignedSites.length })"
              />
            </div>
          </div>
        </div>
        <div
          class="d-flex flex-column mt-4"
          v-if="
            routineDefinition.frequency ||
              routineDefinition.type ||
              categories.length
          "
        >
          <p class="overline blue-grey--text text--darken-3">
            {{ $t('routines.attribute_other') }}
          </p>
          <div class="d-flex">
            <div
              v-if="routineDefinition.frequency"
              class="d-flex flex-column me-4"
            >
              <p class="caption blue-grey--text text--darken-1">
                {{ $t('routines.frequency_label') }}
              </p>
              <div>
                <v-chip
                  small
                  outlined
                  color="blue-grey lighten-3"
                  class="chip caption blue-grey--text text--darken-3 cursor-default mb-1"
                >
                  {{ $t(`routines.frequency.${routineDefinition.frequency}`) }}
                </v-chip>
              </div>
            </div>
            <div v-if="routineDefinition.type" class="d-flex flex-column me-4">
              <p class="caption blue-grey--text text--darken-1">
                {{ $t('routines.routine_type') }}
              </p>
              <div>
                <v-chip
                  small
                  outlined
                  color="blue-grey lighten-3"
                  class="chip caption blue-grey--text text--darken-3 cursor-default mb-1"
                >
                  {{ $t(`routines.type.${routineDefinition.type}`) }}
                </v-chip>
              </div>
            </div>
            <div v-if="categories.length" class="d-flex flex-column">
              <p class="caption blue-grey--text text--darken-1">
                {{
                  $t('routines.category_count', {
                    count: categories.length,
                  })
                }}
              </p>
              <ChipList
                outlined
                :items="categories"
                :limit="1"
                @click="trackAllItems({ categories: categories.length })"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-space-between flex-column align-end">
        <div>
          <Menu :options="menuOptions" v-if="menuOptions.length > 1">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                icon
                color="blue-grey darken-1"
                @click.stop.prevent="openMenu"
              >
                <v-icon size="18">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
          </Menu>
        </div>
        <v-btn
          color="blue"
          rounded
          small
          text
          :ripple="false"
          @click="viewAllDetails"
        >
          {{ $t('routines.view_all_details') }}
        </v-btn>
      </div>
    </div>
    <DeleteConfirmationModal
      :show="showDeleteDialog"
      :title="$t('routines.delete_dialog_title')"
      :text="$t('routines.delete_routine_body')"
      @cancel="showDeleteDialog = false"
      @delete="$emit('delete')"
    />
    <RoutineReminderModal
      v-if="showRoutineReminderDialog"
      :show="showRoutineReminderDialog"
      :isEditMode="hasReminder"
      :reminderId="reminderId"
      :recurrence="reminderRecurrence"
      :reminder="reminder"
      :routineDefinitionId="routineDefinitionId"
      :isRepeatedReminder="isRepeatedReminder"
      @deleteReminder="handleDeleteReminder"
      @saveReminder="fetchReminder"
      @close="showRoutineReminderDialog = false"
    />
    <CannotDeleteDialog
      :open="showCannotDeleteDialog"
      @cancel="showCannotDeleteDialog = false"
    />
  </v-card>
  <v-card elevation="2" class="pa-6 d-flex" v-else>
    <div class="d-flex flex-column justify-space-between" style="width: 40%;">
      <div>
        <v-skeleton-loader type="chip" />
      </div>
      <v-skeleton-loader type="text@4" />
      <div>
        <v-skeleton-loader type="chip" />
      </div>
    </div>
    <v-divider vertical class="mx-6" />
    <div style="width: 60%">
      <div class="d-flex flex-column" style="width: 100%">
        <div class="d-flex justify-space-between">
          <v-skeleton-loader type="text" width="80px" />
          <div class="mt-n2 me-n2">
            <v-skeleton-loader type="chip" />
          </div>
        </div>

        <div>
          <div class="d-flex">
            <div class="d-flex flex-column me-4">
              <v-skeleton-loader type="text" width="90px" />
              <v-skeleton-loader type="avatar" />
            </div>
            <div v-if="assignedTeams.length" class="d-flex flex-column me-4">
              <v-skeleton-loader type="text" />
              <v-skeleton-loader type="chip" />
            </div>
            <div v-if="assignedSites.length" class="d-flex flex-column">
              <v-skeleton-loader type="text" />
              <v-skeleton-loader type="chip" />
            </div>
          </div>
        </div>
        <div class="d-flex flex-column mt-4">
          <v-skeleton-loader type="text" width="80px" />

          <div class="d-flex">
            <div class="d-flex flex-column me-4">
              <v-skeleton-loader type="text" />
              <v-skeleton-loader type="chip" />
            </div>
            <div class="d-flex flex-column me-4">
              <v-skeleton-loader type="text" />
              <v-skeleton-loader type="chip" />
            </div>
            <div class="d-flex flex-column">
              <v-skeleton-loader type="text" />
              <v-skeleton-loader type="chip" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import mixin from '@/mixins/mixin'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import { mapGetters } from 'vuex'
import moment from 'moment'
import _ from 'lodash'

import api from '@/api'
import { FREQUENCY_TYPE } from '@/lib/frequencies'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'

import ReminderChip from '@/containers/RoutineDefinitionsView/RoutineReminder/ReminderChip.vue'
import RoutineDefinitionDialog from './RoutineDetailsDialog.vue'
import AssigneesSelection from '@/components/AssigneesSelector/AssigneesSelection.vue'
import ChipList from '@/components/ChipList.vue'
import Menu from '@/components/UI/Menu.vue'
import DeleteConfirmationModal from '../../components/Modals/DeleteConfirmationModal.vue'
import RoutineReminderModal from '@/containers/RoutineDefinitionsView/RoutineReminder/RoutineReminderModal.vue'
import CannotDeleteDialog from '../../components/Routines/CannotDeleteDialog.vue'

export default {
  name: 'RoutineDetailsCard',

  mixins: [mixin, trackerMixin, routineDefinitionPermissions],

  components: {
    ReminderChip,
    AssigneesSelection,
    ChipList,
    Menu,
    RoutineReminderModal,
    RoutineDefinitionDialog,
    DeleteConfirmationModal,
    CannotDeleteDialog,
  },

  props: {
    loading: Boolean,
    hasRoutineExecutions: Boolean,
    routineDefinition: {
      type: Object,
      required: false,
    },
  },

  data() {
    return {
      showDeleteDialog: false,
      isReminderLoading: false,
      showCannotDeleteDialog: false,
      showDetailsDialog: false,
      reminder: [],
      showRoutineReminderDialog: false,
      loadingReminder: true,
    }
  },

  watch: {
    routineDefinition: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (!_.isEmpty(newValue)) {
          this.fetchReminder()
        }
      },
    },
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId']),

    assignees() {
      return this.routineDefinition?.assignees ?? []
    },

    hasAssignees() {
      return Boolean(this.routineDefinition?.assignees?.length)
    },

    routineDefinitionId() {
      return this.routineDefinition?.id ?? ''
    },

    menuOptions() {
      return _.compact([
        this.canEditRoutine && {
          icon: 'mdi-pencil-outline',
          testId: 'routineDefinition-button-edit',
          tourId: 'routineDefinition-button-edit',
          id: 'edit',
          title: this.$t('common.button.edit'),
          action: () => {
            this.trackRoutineViewNavigate({
              option: 'editRoutine',
              action: 'menu',
              source: 'routineDetail',
              id: this.routineDefinitionId,
            })
            this.navigateTo({
              name: 'editRoutine',
              params: { routineDefinitionId: this.routineDefinitionId },
            })
          },
        },
        this.canCreateRoutine && {
          title: this.$t('common.button.duplicate'),
          testId: 'routineDefinition-button-duplicate',
          tourId: 'routineDefinition-button-duplicate',
          icon: 'mdi-content-copy',
          action: () => {
            this.trackRoutineViewNavigate({
              option: 'duplicate',
              action: 'menu',
              source: 'routineDetail',
              id: this.routineDefinitionId,
            })
            this.navigateTo({
              name: 'createRoutineDefinition',
              params: {
                routineDefinitionId: this.routineDefinitionId,
                duplicate: true,
              },
            })
          },
        },
        this.canDeleteRoutine && {
          title: this.$t('common.button.delete'),
          testId: 'routineDefinition-button-delete',
          tourId: 'routineDefinition-button-delete',
          icon: 'mdi-delete-outline',
          action: () => {
            this.trackRoutineViewNavigate({
              option: 'delete',
              action: 'menu',
              source: 'routineDetail',
              id: this.routineDefinitionId,
            })
            this.handleDeleteRoutine()
          },
        },
      ])
    },

    canCreateRoutine() {
      return this.getRoutineDefinitionPermissions({}).create
    },

    canDeleteRoutine() {
      return this.getRoutineDefinitionPermissions({
        routineDefinition: this.routineDefinition,
      }).delete
    },

    canStartRoutine() {
      return (
        this.routineDefinition.isActive &&
        this.getRoutineDefinitionPermissions({
          routineDefinition: this.routineDefinition,
        }).start
      )
    },

    canEditRoutine() {
      return this.getRoutineDefinitionPermissions({
        routineDefinition: this.routineDefinition,
      }).edit
    },

    reminderId() {
      return this.reminder[0]?._id ?? this.reminder[0]?.id ?? null
    },

    hasReminder() {
      return Boolean(this.reminder?.length > 0)
    },

    reminderRecurrence() {
      return this.reminder[0]?.recurrence ?? {}
    },

    assignedTeams() {
      return (
        this.routineDefinition?.assignedTeams?.map(team => team?.name) ?? []
      )
    },

    assignedSites() {
      return (
        this.routineDefinition?.assignedSites?.map(site => site?.name) ?? []
      )
    },

    categories() {
      return this.routineDefinition?.categories?.map(
        category => this.$t(`common.categories.${category}`) ?? []
      )
    },

    isRepeatedReminder() {
      if (_.isEmpty(this.reminderRecurrence)) return false
      const untilMoment = moment(this.reminderRecurrence?.until)
      const startMoment = moment(this.reminderRecurrence?.startDate)

      const isNotDaily =
        this.reminderRecurrence.frequency !== FREQUENCY_TYPE.DAILY
      const multipleIntervals = this.reminderRecurrence.interval > 1

      return (
        !this.reminderRecurrence?.until ||
        isNotDaily ||
        multipleIntervals ||
        !untilMoment.isSame(startMoment)
      )
    },
  },

  methods: {
    async fetchReminder() {
      try {
        this.isReminderLoading = true
        const response = await api.reminder.getReminder(this.organizationId, {
          filters: {
            account: this.accountId,
            entityType: 'routineDefinition',
            entityId: this.routineDefinition?.id,
          },
        })

        this.reminder = response.data?.results ?? []
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isReminderLoading = false
      }
    },

    async handleDeleteReminder(reminderId) {
      try {
        this.isReminderLoading = true
        await api.reminder.deleteReminder(this.organizationId, reminderId)
        this.showRoutineReminderDialog = false
        await this.fetchReminder()
        this.showSuccess(this.$t('routines_reminder.deleted_successfully'))
        this.trackRoutineDefinitionReminder('reminder.delete', {
          process: 'delete',
          entityType: 'routineDefinition',
          reminderId: reminderId,
        })
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isReminderLoading = false
      }
    },

    async handleDeleteRoutine() {
      if (this.hasRoutineExecutions) {
        this.showCannotDeleteDialog = true
      } else {
        this.showDeleteDialog = true
      }
    },

    showRoutineReminder() {
      this.trackRoutineViewNavigate({
        action: 'openReminderModal',
        source: 'routineDetail',
      })
      this.showRoutineReminderDialog = true
    },

    viewAllDetails() {
      this.showDetailsDialog = true
      this.trackRoutineViewNavigate({
        action: 'viewAllDetails',
        id: this.routineDefinition?.id,
      })
    },

    openMenu() {
      this.trackRoutineViewNavigate({
        action: 'openMenu',
        source: 'routineDetail',
        id: this.routineDefinition?.id,
      })
    },

    trackAllItems(data) {
      this.trackViewAllItems({
        ...data,
        source: 'routineDetail',
        entityType: 'routine',
      })
    },
  },
}
</script>
